import Phaser from 'phaser';
import { GAME_WIDTH } from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';
import CanvasInput from 'phaser3-rex-plugins/plugins/gameobjects/canvas/canvasinput/CanvasInput';
import { JoinConfig } from '../../../utils/UrlStateManager';

/**
 * Manages the name and room code input fields.
 */
export class InputController {
  private container: Phaser.GameObjects.Container;
  private nameInput: CanvasInput;
  private nameLabel: Phaser.GameObjects.Text;
  private roomInput: CanvasInput;
  private roomLabel: Phaser.GameObjects.Text;

  constructor(private scene: Phaser.Scene) {
    this.container = this.scene.add.container(0, 0);
    this.nameLabel = this.createLabel(185, 'Your Name');
    this.nameInput = this.createNameInput();
    this.roomLabel = this.createLabel(255, 'Room Code');
    this.roomInput = this.createRoomInput();
    // Add all elements to container for crossfade transitions
    this.container.add([this.nameLabel, this.nameInput, this.roomLabel, this.roomInput]);
  }

  private createLabel(y: number, text: string): Phaser.GameObjects.Text {
    return this.scene.add
      .text(GAME_WIDTH / 2, y, text, {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '14px',
        color: PERSIAN_THEME.hex.textSecondary,
      })
      .setOrigin(0.5);
  }

  private createNameInput(): CanvasInput {
    return this.scene.add.rexCanvasInput({
      x: GAME_WIDTH / 2,
      y: 215,
      width: 220,
      height: 40,
      style: {
        fontSize: '18px',
        fontFamily: 'Arial, sans-serif',
        color: '#333333',
      },
      wrap: {
        hAlign: 'center',
      },
      background: {
        color: 0xf4e8d3,
        stroke: PERSIAN_THEME.colors.bronze,
        strokeThickness: 3,
        cornerRadius: 8,
      },
      focusStyle: {
        stroke: PERSIAN_THEME.colors.gold,
      },
      maxLength: 12,
    });
  }

  private createRoomInput(): CanvasInput {
    return this.scene.add.rexCanvasInput({
      x: GAME_WIDTH / 2,
      y: 285,
      width: 220,
      height: 40,
      style: {
        fontSize: '18px',
        fontFamily: 'Arial, sans-serif',
        color: '#333333',
      },
      wrap: {
        hAlign: 'center',
      },
      background: {
        color: 0xf4e8d3,
        stroke: PERSIAN_THEME.colors.bronze,
        strokeThickness: 3,
        cornerRadius: 8,
      },
      focusStyle: {
        stroke: PERSIAN_THEME.colors.gold,
      },
      maxLength: 8,
    });
  }

  getName(): string {
    return this.nameInput.text.trim() || 'Player';
  }

  getRoomCode(): string {
    return this.roomInput.text.trim().toUpperCase();
  }

  setName(name: string): void {
    this.nameInput.setText(name);
  }

  setRoomCode(code: string): void {
    this.roomInput.setText(code);
  }

  clearRoomCode(): void {
    this.roomInput.setText('');
  }

  prefill(config: JoinConfig): void {
    // Pre-fill name input
    if (config.playerName && config.playerName !== 'Player') {
      this.nameInput.setText(config.playerName);
    }

    // Pre-fill room input if room param present (not vanity)
    if (config.roomId && !config.isVanity) {
      this.roomInput.setText(config.roomId);
    }
  }

  show(): void {
    this.container.setVisible(true);
  }

  hide(): void {
    this.container.setVisible(false);
  }

  isActive(): boolean {
    return this.nameInput?.active && this.roomInput?.active;
  }

  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  destroy(): void {
    this.container?.destroy();
  }
}
